import LinkIcon from "@/components/icons/link.icon";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { LeadCapture } from "./answers/answerLeadCapture.component";
import { MultipleChoice } from "./answers/answerMultipleChoice.component";
import { NumberInput } from "./answers/answerNumberInput.component";
import { SingleChoice } from "./answers/answerSingleChoice.component";
import { AnswerProps, QuizQuestionData } from "./quizFlow.types";

export const QuizQuestion = ({
  question,
  onSingleChoiceSelect,
  onMultipleChoiceNext,
  onNumberInputNext,
  onLeadCaptureSubmit,
  onLeadCaptureSkip,
  initialAnswer,
}: QuizQuestionProps) => {
  const AnswerComponent = AnswerComponents[question.answerType];

  return (
    <div
      data-testid="quiz-question"
      className="flex flex-col items-center gap-10">
      <div className="flex flex-col items-center gap-4">
        <div
          data-testid="quiz-question-title"
          className="flex max-w-2xl flex-col gap-3 text-center">
          <RichText>{question.title}</RichText>
        </div>

        {question.resourceLink?.url && question.resourceLink?.label && (
          <a
            href={question.resourceLink.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quiz-question-resource-link"
            className="text-greyscale-body flex cursor-pointer items-center gap-1 text-sm font-thin underline underline-offset-2">
            {question.resourceLink.label}
            <LinkIcon width={16} height={16} />
          </a>
        )}
      </div>

      <div className="flex w-full flex-col items-center gap-4">
        {question.selectionInstruction && (
          <Text
            variant="body6"
            data-testid="quiz-question-instruction "
            className="font-thin">
            {question.selectionInstruction}
          </Text>
        )}

        <div className="w-full">
          {AnswerComponent && (
            <AnswerComponent
              question={question}
              onSingleChoiceSelect={onSingleChoiceSelect}
              onMultipleChoiceNext={onMultipleChoiceNext}
              onNumberInputNext={onNumberInputNext}
              onLeadCaptureSubmit={onLeadCaptureSubmit}
              onLeadCaptureSkip={onLeadCaptureSkip}
              initialAnswer={initialAnswer}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AnswerComponents: Record<string, React.ComponentType<AnswerProps>> = {
  singleChoice: SingleChoice,
  multipleChoice: MultipleChoice,
  numberInput: NumberInput,
  leadCapture: LeadCapture,
};

type QuizQuestionProps = {
  question: QuizQuestionData;
  onSingleChoiceSelect: (optionKey: string) => void;
  onMultipleChoiceNext: (selectedKeys: string[]) => void;
  onNumberInputNext: (value: number) => void;
  onLeadCaptureSubmit: (data: { email: string; phone: string }) => void;
  onLeadCaptureSkip: () => void;
  initialAnswer?: string | string[] | number | { email: string; phone: string };
};
